import type { PromotionMatrixRule } from 'types/apis/configToolsSuite';

export type DeploymentAction = 'PUSH' | 'CREATE_PR';

export type EffectivePolicy = {
  allowed: boolean;
  action: DeploymentAction;
  reason: string;
};

type EnvLike = { id?: any; environment_name?: string; environmentName?: string; name?: string; type?: string; stack_name?: string; stackName?: string };

/** Read promotion matrix rule properties with fallback for snake_case API responses. */
export function getRuleProp<T>(r: any, camelKey: string, snakeKey: string, fallback: T): T {
  const v = r?.[camelKey] ?? r?.[snakeKey];
  return v !== undefined && v !== null ? (v as T) : fallback;
}

function normalizeMatrixName(input: string | null | undefined): string {
  return String(input || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function getEnvMatrixCandidates(env: EnvLike | undefined): string[] {
  if (!env) return [];
  const name = (env as any).environment_name || env.environmentName || env.name || '';
  const type = (env as any).type || '';
  const stack = (env as any).stack_name || env.stackName || '';
  return [String(name), String(type), String(stack)].filter(Boolean);
}

/**
 * Find the best matching promotion matrix rule for the given source/target environment pair.
 * Rules are matched by sourceEnvType/targetEnvType against env name, type, and stack.
 */
export function findBestMatrixRule(
  rules: PromotionMatrixRule[],
  sourceEnv: EnvLike | undefined,
  targetEnv: EnvLike | undefined
): PromotionMatrixRule | null {
  if (!sourceEnv || !targetEnv) return null;

  const srcCandidates = getEnvMatrixCandidates(sourceEnv).map(normalizeMatrixName);
  const tgtCandidates = getEnvMatrixCandidates(targetEnv).map(normalizeMatrixName);

  const activeRules = (rules || []).filter((r) => {
    if (!r) return false;
    const isActive = getRuleProp(r, 'isActive', 'is_active', true);
    return String(isActive).toLowerCase() !== 'false';
  });

  let best: { rule: PromotionMatrixRule; score: number } | null = null;
  for (const r of activeRules) {
    const srcRule = normalizeMatrixName(getRuleProp(r, 'sourceEnvType', 'source_env_type', ''));
    const tgtRule = normalizeMatrixName(getRuleProp(r, 'targetEnvType', 'target_env_type', ''));
    if (!srcRule || !tgtRule) continue;

    const srcMatch =
      srcCandidates.includes(srcRule) ||
      srcCandidates.some((c) => c.includes(srcRule) || srcRule.includes(c));
    const tgtMatch =
      tgtCandidates.includes(tgtRule) ||
      tgtCandidates.some((c) => c.includes(tgtRule) || tgtRule.includes(c));

    if (!srcMatch || !tgtMatch) continue;

    const score =
      (srcCandidates.includes(srcRule) ? 3 : 1) +
      (tgtCandidates.includes(tgtRule) ? 3 : 1);

    if (!best || score > best.score) best = { rule: r, score };
  }

  return best?.rule || null;
}

/**
 * Compute the effective sync policy (allowed action and reason) based on the matched promotion matrix rule.
 */
export function computeEffectivePolicy(matchedRule: PromotionMatrixRule | null): EffectivePolicy {
  if (!matchedRule) {
    return { allowed: false, action: 'PUSH', reason: 'No promotion matrix rule for this environment pair.' };
  }

  const viewAccess = getRuleProp(matchedRule, 'viewAccess', 'view_access', false);
  if (!viewAccess) {
    return { allowed: false, action: 'PUSH', reason: 'View access is not allowed for this environment pair (promotion matrix).' };
  }

  const shellRequired = !!getRuleProp(matchedRule, 'shellBundleRequired', 'shell_bundle_required', false);
  const allowDirectPush = !!getRuleProp(matchedRule, 'allowDirectPush', 'allow_direct_push', false);
  const allowShellBundle = !!getRuleProp(matchedRule, 'allowShellBundle', 'allow_shell_bundle', false);
  const allowPush = allowDirectPush && !shellRequired;
  const allowShell = allowShellBundle;

  if (shellRequired) {
    return allowShell
      ? { allowed: true, action: 'CREATE_PR', reason: 'Shell bundle is required by promotion matrix.' }
      : { allowed: false, action: 'CREATE_PR', reason: 'Shell bundle required but not allowed by promotion matrix.' };
  }

  if (allowPush) return { allowed: true, action: 'PUSH', reason: '' };
  if (allowShell) return { allowed: true, action: 'CREATE_PR', reason: '' };

  return { allowed: false, action: 'PUSH', reason: 'Sync not allowed by promotion matrix.' };
}
